import { isUndefined } from 'lodash';
import axios from 'src/utils/axios';
import logger from 'src/utils/logger';
import parse from 'src/utils/parse';

export type SubscriptionEntityType = 'PRODUCT' | 'PACKAGE';

export type SubscriptionTypeApi = 'MONTHLY' | 'YEARLY';

export type CreateSubscriptionProductPayload = {
  entityType: SubscriptionEntityType;
  productId: string;
  numberOfLicenses: number;
};

export type CreateSubscriptionPayload = {
  /** Omit to let BE auto-generate; if sent, BE uses this value. */
  subscriptionOrderNumber?: string;
  customerReference?: string;
  subscriptionType: SubscriptionTypeApi;
  clientId: string;
  startDate: string;
  internalNotes?: string;
  products: CreateSubscriptionProductPayload[];
};

export type ListSubscriptionsParams = {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
  clientId?: string;
  resellerId?: string;
  entityType?: string;
};

const baseUrl = (): string => {
  const url = process.env.REACT_APP_SUBSCRIPTION;
  if (!url) {
    throw new Error('REACT_APP_SUBSCRIPTION environment variable is not defined');
  }
  return url.replace(/\/$/, '');
};

/** Parse list envelopes: array | { data } | { content } | nested total fields. */
export const parseSubscriptionsList = (
  response: any,
): { items: any[]; total: number } => {
  const body = response?.data;
  if (Array.isArray(body)) {
    return { items: body, total: body.length };
  }
  if (!body || typeof body !== 'object') {
    return { items: [], total: 0 };
  }

  const items = Array.isArray(body.data)
    ? body.data
    : Array.isArray(body.content)
      ? body.content
      : Array.isArray(body.items)
        ? body.items
        : [];

  const total =
    body.totalRecords ??
    body.totalElements ??
    body.total ??
    body.count ??
    items.length;

  return { items, total: Number(total) || 0 };
};

class SubscriptionsApi {
  async getSubscriptions(params: ListSubscriptionsParams = {}): Promise<any> {
    return new Promise((resolve, reject) => {
      const query: Record<string, string | number> = {};
      if (params.page != null) query.page = params.page;
      if (params.size != null) query.size = params.size;
      if (params.search) query.search = params.search;
      if (params.status) query.status = params.status;
      if (params.clientId) query.clientId = params.clientId;
      if (params.resellerId) query.resellerId = params.resellerId;
      if (params.entityType) query.entityType = params.entityType;

      axios
        .get(`${baseUrl()}/subscriptions`, { params: query })
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async createSubscription(payload: CreateSubscriptionPayload): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${baseUrl()}/subscriptions`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getSubscriptionById(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .get(`${baseUrl()}/subscriptions/${id}`)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async updateSubscription(id: string, payload: Partial<CreateSubscriptionPayload>): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .patch(`${baseUrl()}/subscriptions/${id}`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async deleteSubscription(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete(`${baseUrl()}/subscriptions/${id}`)
        .then((response) => {
          response = parse(response);
          resolve(response);
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async extendSubscription(id: string, payload?: Record<string, unknown>): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${baseUrl()}/subscriptions/${id}/extend`, payload ?? {})
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }
}

export const subscriptionsApi = new SubscriptionsApi();
